import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  UserPlus, 
  Edit2, 
  Trash2, 
  Save, 
  X, 
  Shield, 
  User,
  Key,
  Calendar
} from 'lucide-react';
import Modal from '../components/Modal';
import { useAuth } from '../contexts/AuthContext';

function Utenti() {
  const { user: currentUser } = useAuth();
  const [utenti, setUtenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    ruolo: 'operatore'
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  useEffect(() => {
    loadUtenti();
  }, []);

  const loadUtenti = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/utenti', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setUtenti(response.data.data);
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Errore nel caricamento degli utenti';
      toast.error(message);
      console.error('Errore caricamento utenti:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        password: '',
        ruolo: user.ruolo
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        password: '',
        ruolo: 'operatore'
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      username: '',
      password: '',
      ruolo: 'operatore'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validazione
    if (!formData.username || formData.username.length < 3) {
      toast.error('Lo username deve essere di almeno 3 caratteri');
      return;
    }

    if (!editingUser && !formData.password) {
      toast.error('La password è obbligatoria per nuovi utenti');
      return;
    }

    if (formData.password && formData.password.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Prepara i dati da inviare
      const dataToSend = {
        username: formData.username,
        ruolo: formData.ruolo
      };

      // Includi la password solo se è stata inserita
      if (formData.password) {
        dataToSend.password = formData.password;
      }

      let response;
      if (editingUser) {
        // Aggiorna utente esistente
        response = await axios.put(
          `/api/utenti/${editingUser.id}`,
          dataToSend,
          { headers }
        );
      } else {
        // Crea nuovo utente
        response = await axios.post('/api/utenti', dataToSend, { headers });
      }

      if (response.data.success) {
        toast.success(response.data.message);
        loadUtenti();
        handleCloseModal();
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Errore durante il salvataggio';
      toast.error(message);
      console.error('Errore salvataggio utente:', error);
    }
  };

  const handleDelete = (id, username) => {
    setUserToDelete({ id, username });
    setShowDeleteModal(true);
  };

  const executeDeleteUser = async () => {
    if (!userToDelete) return;

    setShowDeleteModal(false);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`/api/utenti/${userToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success(response.data.message);
        loadUtenti();
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Errore durante l\'eliminazione';
      toast.error(message);
      console.error('Errore eliminazione utente:', error);
    } finally {
      setUserToDelete(null);
    }
  };

  const getRuoloBadge = (ruolo) => {
    if (ruolo === 'admin') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <Shield className="w-3 h-3 mr-1" />
          Admin
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        <User className="w-3 h-3 mr-1" />
        Operatore
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestione Utenti</h1>
          <p className="mt-1 text-sm text-gray-500">
            Crea e gestisci gli utenti che possono accedere al sistema
          </p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <UserPlus className="h-5 w-5 mr-2" />
          Nuovo Utente
        </button>
      </div>

      {/* Vista Mobile - Card */}
      <div className="block sm:hidden space-y-4">
        {utenti.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <p className="text-sm text-gray-500">Nessun utente trovato</p>
          </div>
        ) : (
          utenti.map((utente) => (
            <div key={utente.id} className="bg-white shadow rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-12 w-12 bg-gray-200 rounded-full flex items-center justify-center">
                    <User className="h-6 w-6 text-gray-500" />
                  </div>
                  <div className="ml-3">
                    <div className="text-base font-medium text-gray-900">
                      {utente.username}
                    </div>
                    {utente.id === currentUser?.id && (
                      <span className="text-xs text-blue-600">(Tu)</span>
                    )}
                  </div>
                </div>
                {getRuoloBadge(utente.ruolo)}
              </div>
              
              <div className="mb-3 flex items-center text-sm text-gray-500">
                <Calendar className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                <span className="break-all">{formatDate(utente.created_at)}</span>
              </div>

              <div className="flex items-center gap-2 pt-3 border-t border-gray-200">
                <button
                  onClick={() => handleOpenModal(utente)}
                  className="flex-1 btn btn-outline btn-sm flex items-center justify-center"
                >
                  <Edit2 className="h-4 w-4 mr-1" />
                  Modifica
                </button>
                <button
                  onClick={() => handleDelete(utente.id, utente.username)}
                  disabled={utente.id === currentUser?.id}
                  className={`flex-1 btn btn-outline btn-sm text-red-600 hover:bg-red-50 flex items-center justify-center ${
                    utente.id === currentUser?.id ? 'opacity-30 cursor-not-allowed' : ''
                  }`}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Elimina
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Vista Desktop - Tabella */}
      <div className="hidden sm:block bg-white shadow overflow-hidden sm:rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Utente
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ruolo
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Data Creazione
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Azioni
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {utenti.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                  Nessun utente trovato
                </td>
              </tr>
            ) : (
              utenti.map((utente) => (
                <tr key={utente.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center">
                        <User className="h-5 w-5 text-gray-500" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {utente.username}
                          {utente.id === currentUser?.id && (
                            <span className="ml-2 text-xs text-blue-600">(Tu)</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getRuoloBadge(utente.ruolo)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                      {formatDate(utente.created_at)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleOpenModal(utente)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                      title="Modifica utente"
                    >
                      <Edit2 className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(utente.id, utente.username)}
                      className="text-red-600 hover:text-red-900"
                      title="Elimina utente"
                      disabled={utente.id === currentUser?.id}
                    >
                      <Trash2 className={`h-5 w-5 ${utente.id === currentUser?.id ? 'opacity-30 cursor-not-allowed' : ''}`} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Creazione/Modifica */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingUser ? 'Modifica Utente' : 'Nuovo Utente'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Username */}
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700">
              Username *
            </label>
            <input
              type="text"
              id="username"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
              placeholder="Es: mario.rossi"
              required
              minLength={3}
            />
            <p className="mt-1 text-xs text-gray-500">
              Minimo 3 caratteri
            </p>
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password {!editingUser && '*'}
              {editingUser && <span className="text-xs text-gray-500"> (lascia vuoto per non modificare)</span>}
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Key className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="password"
                id="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="block w-full pl-10 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
                placeholder="••••••"
                required={!editingUser}
                minLength={6}
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Minimo 6 caratteri
            </p>
          </div>

          {/* Ruolo */}
          <div>
            <label htmlFor="ruolo" className="block text-sm font-medium text-gray-700">
              Ruolo *
            </label>
            <select
              id="ruolo"
              value={formData.ruolo}
              onChange={(e) => setFormData({ ...formData, ruolo: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
              required
            >
              <option value="operatore">Operatore</option>
              <option value="admin">Admin</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              <strong>Operatore:</strong> accesso limitato alle funzionalità operative<br />
              <strong>Admin:</strong> accesso completo al sistema
            </p>
          </div>

          {/* Pulsanti */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleCloseModal}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <X className="h-4 w-4 mr-2" />
              Annulla
            </button>
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Save className="h-4 w-4 mr-2" />
              {editingUser ? 'Aggiorna' : 'Crea Utente'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modale di conferma eliminazione */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setUserToDelete(null);
        }}
        title="Conferma Eliminazione"
        message={`Sei sicuro di voler eliminare l'utente "${userToDelete?.username}"?\n\nQuesta azione non può essere annullata e l'utente perderà l'accesso al sistema.`}
        type="warning"
        confirmText="Sì, Elimina"
        cancelText="Annulla"
        onConfirm={executeDeleteUser}
      />
    </div>
  );
}

export default Utenti;

